import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, PencilIcon, XIcon } from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { SessionDetail } from "../../../../shared/api.ts";
import { formatCode } from "../../../../shared/uktzed.ts";
import { Code } from "@/components/common/code.tsx";
import { MarkdownText } from "@/components/common/markdown.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ConfirmDialog } from "@/components/ui/confirm-dialog.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { errorMessage } from "@/lib/hooks.ts";
import { cn } from "@/lib/utils.ts";
import { CodePickerPopover } from "./code-picker.tsx";
import { PanelSection } from "./section.tsx";

type DeclaredCodeValue = {
  declaredCode: string | null;
  readOnly: boolean;
  saving: boolean;
  /** Sets the declared code; null clears it. */
  save: (code: string | null) => void;
};

const DeclaredCodeContext = createContext<DeclaredCodeValue | null>(null);

/**
 * The declared code of one session, for the declared-code section and every code card.
 * Saving uses `PUT /api/sessions/:id/declared-code` and shows the new code at once.
 */
export function DeclaredCodeProvider({
  sessionId,
  declaredCode,
  readOnly,
  children,
}: {
  sessionId: string;
  declaredCode: string | null;
  readOnly: boolean;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (code: string | null) => api.setDeclaredCode(sessionId, { code }),
    onMutate: async (code) => {
      // Show the new code at once; the refetch below confirms it.
      queryClient.setQueryData<SessionDetail>(queryKeys.session(sessionId), (detail) =>
        detail ? { ...detail, session: { ...detail.session, declaredCode: code } } : detail,
      );
    },
    onSuccess: (_data, code) => {
      toast.success(code ? t.panel.declaredSaved : t.panel.declaredCleared);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
  const value = useMemo(() => ({ declaredCode, readOnly, saving: isPending, save: mutate }), [declaredCode, readOnly, isPending, mutate]);
  return <DeclaredCodeContext.Provider value={value}>{children}</DeclaredCodeContext.Provider>;
}

function useDeclaredCode(): DeclaredCodeValue {
  const value = useContext(DeclaredCodeContext);
  if (!value) {
    throw new Error("useDeclaredCode must be used inside DeclaredCodeProvider");
  }
  return value;
}

/** True when this code is the session's declared code. */
export function useIsDeclared(code: string): boolean {
  return useDeclaredCode().declaredCode === code;
}

/**
 * The pick action of a code card. "Use this code" makes it the declared code; on the declared card the action
 * becomes the "Declared" badge with Clear. A code the agent ruled out asks first and repeats why.
 */
export function DeclareCodeAction({ code, ruledOutReason, className }: { code: string; ruledOutReason?: string; className?: string }) {
  const { t } = useI18n();
  const { declaredCode, readOnly, saving, save } = useDeclaredCode();
  const [confirming, setConfirming] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  const declared = declaredCode === code;

  // A pick or a clear swaps the button for the other one. Focus follows once the save settles, instead of dropping to the page.
  useEffect(() => {
    if (!saving && refocus.current) {
      refocus.current = false;
      button.current?.focus();
    }
  }, [saving, declared]);

  const declare = (next: string | null) => {
    refocus.current = true;
    save(next);
  };

  if (readOnly && !declared) return null;
  const ruledOut = ruledOutReason !== undefined;
  const label = ruledOut ? t.panel.useRuledOut : t.panel.useThisCode;
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {declared ? (
        <>
          <Badge variant="success">
            <CheckIcon aria-hidden />
            {t.panel.declaredMark}
          </Badge>
          {!readOnly ? (
            <Button ref={button} variant="ghost-muted" size="xs" disabled={saving} aria-label={`${t.common.clear} ${formatCode(code)}`} onClick={() => declare(null)}>
              <XIcon aria-hidden />
              {t.common.clear}
            </Button>
          ) : null}
        </>
      ) : (
        // Every card has this button, so its name carries the code.
        <Button ref={button} variant="outline" size="xs" disabled={saving} aria-label={`${label} ${formatCode(code)}`} onClick={() => (ruledOut ? setConfirming(true) : declare(code))}>
          {label}
        </Button>
      )}
      {ruledOut && !readOnly ? (
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title={t.panel.ruledOutTitle}
          description={t.panel.ruledOutText(formatCode(code))}
          confirmLabel={t.panel.useThisCode}
          cancelLabel={t.common.cancel}
          destructive={false}
          pending={saving}
          onConfirm={() => {
            declare(code);
            setConfirming(false);
          }}
        >
          {ruledOutReason ? (
            <div className="rounded-lg border border-warning-border bg-warning-soft px-3 py-2.5">
              <p className="mb-0.5 text-[11.5px] font-semibold text-warning">{t.panel.ruledOut}</p>
              <MarkdownText text={ruledOutReason} size="sm" />
            </div>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/** "Declared code": the code put in box 33. Code cards set it too, through {@link DeclareCodeAction}. */
export function DeclaredCodeSection({ chosenCode }: { chosenCode: string | null }) {
  const { t } = useI18n();
  const { declaredCode, readOnly, saving, save } = useDeclaredCode();
  const [picking, setPicking] = useState(false);

  if (readOnly && !declaredCode) return null;

  return (
    <PanelSection title={t.panel.declaredCode}>
      {declaredCode ? (
        <div className="flex flex-col gap-2.5 rounded-xl border bg-card p-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <Code value={declaredCode} className="text-[17px] font-semibold" />
            {chosenCode ? (
              declaredCode === chosenCode ? (
                <Badge variant="success">
                  <CheckIcon aria-hidden />
                  {t.panel.declaredMatches}
                </Badge>
              ) : (
                <Badge variant="warning">{t.panel.declaredDiffers}</Badge>
              )
            ) : null}
          </div>
          {!readOnly ? (
            <div className="flex gap-2">
              <CodePickerPopover open={picking} onOpenChange={setPicking} onPick={(line) => save(line.code)}>
                <Button variant="outline" size="xs">
                  <PencilIcon aria-hidden />
                  {t.common.change}
                </Button>
              </CodePickerPopover>
              <Button variant="ghost-muted" size="xs" disabled={saving} onClick={() => save(null)}>
                <XIcon aria-hidden />
                {t.common.clear}
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-xl border border-dashed bg-card/60 p-3.5">
          <p className="text-[12.5px] text-muted-foreground">{t.panel.declaredHint}</p>
          <div className="flex flex-wrap gap-2">
            {chosenCode ? (
              <Button size="sm" disabled={saving} onClick={() => save(chosenCode)}>
                {t.panel.useChosen}
              </Button>
            ) : null}
            <CodePickerPopover open={picking} onOpenChange={setPicking} onPick={(line) => save(line.code)}>
              <Button variant="outline" size="sm">
                {t.panel.anotherCode}
              </Button>
            </CodePickerPopover>
          </div>
        </div>
      )}
    </PanelSection>
  );
}
